import { addMonths, getMonth, format } from 'date-fns';

/** 1–12 as numeric keys */
type MonthIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

type ImpoundScheduleRow = {
  state: string; // or narrow to a union of state codes if you like
} & Record<MonthIndex, number>;

type ImpoundSchedule = ImpoundScheduleRow[];

/** Optional guard to narrow a number to MonthIndex */
function isMonthIndex(n: number): n is MonthIndex {
  return Number.isInteger(n) && n >= 1 && n <= 12;
}

/** Convert a Date to MonthIndex (1–12) */
function toMonthIndex(d: Date): MonthIndex {
  const m = getMonth(addMonths(d, 1)) + 1; // getMonth is 0–11
  if (!isMonthIndex(m)) throw new Error('Invalid month');
  return m;
}

/** Find a row by state code, e.g., 'AL' */
function findRow(schedule: ImpoundSchedule, state: string): ImpoundScheduleRow | undefined {
  return schedule.find(r => r.state === state);
}

/** Main lookup */
export function getMonthsToCollect(state: string): number {
  const currentMonth: MonthIndex = Number(format(addMonths(new Date(), 1), 'M')) as MonthIndex; // 1–12
  const row = findRow(impoundSchedule, state);
  return row ? row[currentMonth] : 4;
}

// number represents the closing month
export const impoundSchedule: ImpoundSchedule = [
  {
    1: 4,
    2: 5,
    3: 6,
    4: 7,
    5: 8,
    6: 9,
    7: 10,
    8: 11,
    9: 12,
    10: 2,
    11: 2,
    12: 3,
    state: 'AL',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'AK',
  },
  {
    1: 6,
    2: 2,
    3: 2,
    4: 3,
    5: 4,
    6: 5,
    7: 6,
    8: 2,
    9: 2,
    10: 3,
    11: 4,
    12: 5,
    state: 'AZ',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'AR',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 3,
    10: 4,
    11: 5,
    12: 6,
    state: 'CA',
  },
  {
    1: 3,
    2: 4,
    3: 2,
    4: 2,
    5: 3,
    6: 4,
    7: 5,
    8: 6,
    9: 7,
    10: 8,
    11: 9,
    12: 4,
    state: 'CO',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'CT',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'DE',
  },
  {
    1: 2,
    2: 3,
    3: 4,
    4: 5,
    5: 6,
    6: 2,
    7: 2,
    8: 3,
    9: 4,
    10: 5,
    11: 6,
    12: 7,
    state: 'DC',
  },
  {
    1: 5,
    2: 6,
    3: 7,
    4: 8,
    5: 9,
    6: 10,
    7: 11,
    8: 12,
    9: 2,
    10: 2,
    11: 3,
    12: 4,
    state: 'FL',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'GA',
  },
  {
    1: 3,
    2: 4,
    3: 5,
    4: 6,
    5: 2,
    6: 2,
    7: 3,
    8: 4,
    9: 5,
    10: 6,
    11: 2,
    12: 2,
    state: 'HI',
  },
  {
    1: 5,
    2: 6,
    3: 2,
    4: 2,
    5: 3,
    6: 4,
    7: 5,
    8: 6,
    9: 2,
    10: 2,
    11: 3,
    12: 4,
    state: 'ID',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'IL',
  },
  {
    1: 6,
    2: 2,
    3: 2,
    4: 3,
    5: 4,
    6: 5,
    7: 6,
    8: 2,
    9: 2,
    10: 3,
    11: 4,
    12: 5,
    state: 'IN',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'IA',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'KS',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'KY',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'LA',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'ME',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MD',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MA',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MI',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MN',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MS',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MO',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'MT',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'NE',
  },
  {
    1: 2,
    2: 3,
    3: 4,
    4: 5,
    5: 3,
    6: 4,
    7: 2,
    8: 3,
    9: 4,
    10: 2,
    11: 3,
    12: 2,
    state: 'NV',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'NH',
  },
  {
    1: 3,
    2: 2,
    3: 2,
    4: 3,
    5: 2,
    6: 2,
    7: 3,
    8: 2,
    9: 2,
    10: 3,
    11: 2,
    12: 2,
    state: 'NJ',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'NM',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'NY',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'NC',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'ND',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'OH',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'OK',
  },
  {
    1: 6,
    2: 7,
    3: 8,
    4: 9,
    5: 10,
    6: 11,
    7: 12,
    8: 2,
    9: 2,
    10: 3,
    11: 4,
    12: 5,
    state: 'OR',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'PA',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'RI',
  },
  {
    1: 4,
    2: 5,
    3: 6,
    4: 7,
    5: 8,
    6: 9,
    7: 10,
    8: 11,
    9: 12,
    10: 2,
    11: 2,
    12: 3,
    state: 'SC',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'SD',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'TN',
  },
  {
    1: 4,
    2: 5,
    3: 6,
    4: 7,
    5: 8,
    6: 9,
    7: 10,
    8: 11,
    9: 12,
    10: 2,
    11: 2,
    12: 3,
    state: 'TX',
  },
  {
    1: 5,
    2: 6,
    3: 7,
    4: 8,
    5: 9,
    6: 10,
    7: 11,
    8: 12,
    9: 2,
    10: 2,
    11: 3,
    12: 4,
    state: 'UT',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'VT',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'VA',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'WA',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'WV',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'WI',
  },
  {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 4,
    11: 5,
    12: 6,
    state: 'WY',
  },
];

// -------------------- Example usage --------------------

// Using today's month (local timezone):
// const monthNow = toMonthIndex(new Date());
// const monthsForAL = getMonthsToCollect('AL');
// console.log({ monthNow, monthsForAL });

// // Or with an explicit month:
// const monthsForALInSeptember = getMonthsToCollect('AL');
// console.log({ monthsForALInSeptember });
